import { validateAddress, ValidationResult } from '@taquito/utils';

/**
 *  @category Error
 *  @description Error that indicates an invalid address being used or passed
 */
class InvalidAddressError extends Error {
    constructor(message) {
        super(message);
    }
}
/**
 *  @category Error
 *  @description Error that indicates invalid script format being useed or passed
 */
class InvalidScriptFormatError extends Error {
    constructor(message) {
        super(message);
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class ReadWrapperContractsLibrary {
    constructor(readProvider, contractslibrary) {
        this.readProvider = readProvider;
        this.contractslibrary = contractslibrary;
    }
    getScript(address, block) {
        return __awaiter(this, void 0, void 0, function* () {
            const contractData = this.contractslibrary.getContract(address);
            if (contractData) {
                return contractData.script;
            }
            else {
                return this.readProvider.getScript(address, block);
            }
        });
    }
    getEntrypoints(contract) {
        return __awaiter(this, void 0, void 0, function* () {
            const contractData = this.contractslibrary.getContract(contract);
            if (contractData) {
                return contractData.entrypoints;
            }
            else {
                return this.readProvider.getEntrypoints(contract);
            }
        });
    }
    getBalance(address, block) {
        return this.readProvider.getBalance(address, block);
    }
    getDelegate(address, block) {
        return this.readProvider.getDelegate(address, block);
    }
    getNextProtocol(block) {
        return this.readProvider.getNextProtocol(block);
    }
    getProtocolConstants(block) {
        return this.readProvider.getProtocolConstants(block);
    }
    getStorage(contract, block) {
        return this.readProvider.getStorage(contract, block);
    }
    getBlockHash(block) {
        return this.readProvider.getBlockHash(block);
    }
    getBlockLevel(block) {
        return this.readProvider.getBlockLevel(block);
    }
    getCounter(pkh, block) {
        return this.readProvider.getCounter(pkh, block);
    }
    getBlockTimestamp(block) {
        return this.readProvider.getBlockTimestamp(block);
    }
    getBigMapValue(bigMapQuery, block) {
        return this.readProvider.getBigMapValue(bigMapQuery, block);
    }
    getSaplingDiffById(saplingStateQuery, block) {
        return this.readProvider.getSaplingDiffById(saplingStateQuery, block);
    }
    getChainId() {
        return this.readProvider.getChainId();
    }
    isAccountRevealed(publicKeyHash, block) {
        return this.readProvider.isAccountRevealed(publicKeyHash, block);
    }
    getBlock(block) {
        return this.readProvider.getBlock(block);
    }
    getLiveBlocks(block) {
        return this.readProvider.getLiveBlocks(block);
    }
}

/**
 * @packageDocumentation
 * @module @taquito/contracts-library
 */
/**
 * @description Allows to specify static data related to contracts (i.e., script and entrypoints) avoiding Taquito to fetch them from the network.
 *
 * @example
 * ```
 * import { ContractsLibrary } from '@taquito/contracts-library';
 * import { TezosToolkit } from '@taquito/taquito';
 *
 * const Tezos = new TezosToolkit('rpcUrl');
 * const contractsLibrary = new ContractsLibrary();
 *
 * contractsLibrary.addContract({
 *      ['contractAddress1']: {
 *          script: script1, // obtained from Tezos.rpc.getContract('contractAddress1').script
 *          entrypoints: entrypoints1 // obtained from Tezos.rpc.getEntrypoints('contractAddress1')
 *      },
 *      // load more contracts
 * });
 *
 * Tezos.addExtension(contractsLibrary);
 * ```
 *
 */
class ContractsLibrary {
    constructor() {
        this._contractsLibrary = {};
    }
    /**
     * @description Saves one of several contract in the library
     *
     * @param contract is an object where the key is a contract address and the value is an object having a script and an entrypoints properties.
     * Note: the expected format for the script and entrypoints properties are the same as the one respectivlely returned by
     * `TezosToolkit.rpc.getContract('contractAddress').script` and `TezosToolkit.rpc.getEntrypoints`
     *
     */
    addContract(contract) {
        for (const contractAddress in contract) {
            this.validateContractAddress(contractAddress);
            this.validateContractScriptFormat(contract[contractAddress].script, contractAddress);
            Object.assign(this._contractsLibrary, {
                [contractAddress]: Object.assign({}, contract[contractAddress]),
            });
        }
    }
    getContract(contractAddress) {
        return this._contractsLibrary[contractAddress];
    }
    configureContext(context) {
        context.registerProviderDecorator((context) => {
            context.readProvider = new ReadWrapperContractsLibrary(context.readProvider, this);
            return context;
        });
    }
    validateContractAddress(address) {
        if (validateAddress(address) !== ValidationResult.VALID) {
            throw new InvalidAddressError(`Address is invalid: ${address}`);
        }
    }
    validateContractScriptFormat(script, address) {
        if (!script.code) {
            throw new InvalidScriptFormatError(`An invalid script property has been provided for ${address}. The script property can be retrieved from TezosToolkit.rpc.getNormalizedScript(${address}). Invalid script: ${script}`);
        }
    }
}

export { ContractsLibrary };
//# sourceMappingURL=taquito-contracts-library.es6.js.map
